'use client';

import type { AriaAttributes } from 'react';
import { useState } from 'react';
import { Dialog } from '../Dialog/Dialog';
import { Image } from '../../components/Image';
import { DialogHeader } from '../Dialog/DialogHeader/DialogHeader';
import { DialogContent } from '../Dialog/DialogContent/DialogContent';

export interface EfficiencyFlagOverlay {
  title: string;
  imgUrl: string;
}

export type EfficiencyFlagOverlayDialogProps = {
  overlay?: EfficiencyFlagOverlay;
  children: React.ReactNode;
  onDialogOpen?: () => void;
} & AriaAttributes;

const EfficiencyFlagOverlayDialogInternal = ({
  overlay,
  children,
  'aria-describedby': ariaDescribedBy,
  onDialogOpen,
}: Required<Pick<EfficiencyFlagOverlayDialogProps, 'overlay'>> &
  Omit<EfficiencyFlagOverlayDialogProps, 'overlay'>) => {
  const [isOpen, setIsOpen] = useState(false);
  const openDialog = () => {
    if (typeof onDialogOpen === 'function') {
      onDialogOpen();
    }
    setIsOpen(true);
  };

  return (
    <>
      <button
        type="button"
        onClick={openDialog}
        className="cursor-pointer appearance-none [line-height:0]"
        aria-describedby={ariaDescribedBy}
      >
        {children}
      </button>
      {isOpen && (
        <Dialog
          isOpen
          onClose={() => setIsOpen(false)}
          title={overlay.title}
          className="md:min-w-[400px]"
          allowBackdropClose
        >
          <DialogHeader onClose={() => setIsOpen(false)}>{overlay.title}</DialogHeader>
          <DialogContent className="py-0">
            <div className="relative flex h-[600px] w-full flex-col items-center justify-center *:object-contain">
              <Image src={overlay.imgUrl} alt={overlay.title} fill unoptimized />
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export const EfficiencyFlagOverlayDialog = ({
  overlay,
  children,
  ...props
}: EfficiencyFlagOverlayDialogProps) =>
  overlay ? (
    <EfficiencyFlagOverlayDialogInternal overlay={overlay} {...props}>
      {children}
    </EfficiencyFlagOverlayDialogInternal>
  ) : (
    children
  );
